import { InternalInfoModel } from '../../models/complex/internal-info/internal-info.model';
import { TaskModel } from '../../models/complex/task.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import { ConfigConstants } from '../../config/config-constants';
import type { AddressState } from '../address/address-collection.state';
import type { ODataLongText } from '../odata-types/multiline-text.type';
import type { ODataNotification } from '../odata-types/notification.type';
import { odataToNotificationLongTextLines } from './odata-to-notification-entities';
import { statusStateToModel } from './notification-collection.converters';
import { NotificationPropertiesSnapshot, NotificationState } from './notification-collection.state';
import {
    notificationCodeToOdata,
    notificationContractRowToOdata,
    notificationDropOffPointToOdata,
    notificationEmailToOdata,
    notificationEquipmentToOdata,
    notificationHistoryEntryToOdata,
    notificationLongTextLinesToOdata,
    notificationPaperworkQuestionToOdata,
    notificationParticipantToOdata,
    notificationPartToOdata,
    notificationTaskToOdata,
    numericField,
} from './notification-entities-to-odata';
import type { NotificationLongTextLineState, NotificationTaskState } from './notification-entities.state';
import { NotificationIdState } from '../utilities/notification-id.type';
import { effectiveStatusForEntry } from './selectors/notification.selector';
import { outboundAddrNumber } from '../address';

/**
 * The one place in the slice that knows `NotificationModel` exists, and the only reason the slice
 * still has to: 51 call sites across the app take or return that model, so until they read the state
 * directly the store has to be able to hand one out — an offline boot from the persisted cache
 * included, where no backend payload is at hand.
 *
 * The model is frozen and takes the `NdpHeaderSet` payload it was always built from, so this rebuilds
 * that payload from the stored notification and the addresses it links to. The store's own converters
 * (`odataToNotificationState`, `notification-longtext.converters.ts`) are what a *new* reader uses;
 * this file exists so no new reader has to, and it is deleted together with the model.
 *
 * The reconstruction is faithful because the slice keeps everything the payload carried that anything
 * reads: the scalars, the expanded rows and the long text lines. Where the conversion normalized a
 * value the model would normalize the same way — the DISP substituted for an empty status, the `'EN'`
 * for a missing language, the numbers and dates it parses back out of strings — the round trip lands
 * on the value the model would have produced from the original payload. The row-by-row half of that
 * is `notification-entities-to-odata.ts`; what is deliberately *not* restored is the OData envelope,
 * which no model reads.
 */

/** Enough of an entity metadata block to satisfy the payload type; the model never reads it. */
const SYNTHETIC_METADATA: ODataNotification['__metadata'] = {
    id: '',
    uri: '',
    type: 'HC_SM_NGM_58_SRV.NdpHeader',
};

/** The `{ results }` wrapper the payload's expanded sets come in. */
function wrap<T>(results: readonly T[]): { results: T[] } {
    return { results: [...results] };
}

/**
 * A set the backend only expands under a config flag, wrapped only when it holds something. An empty
 * one is left out rather than wrapped: the model branches on the *presence* of these sets — an
 * expanded-but-empty `IssueRepLtextSet` would make it ask the config service about a text that is not
 * there — so "nothing stored" has to read back as "not expanded".
 *
 * @param results the stored rows
 */
function wrapOptional<T>(results: readonly T[]): { results: T[] } | undefined {
    return results.length > 0 ? wrap(results) : undefined;
}

/**
 * Rebuilds the `NdpHeaderSet` payload of a stored notification, for the frozen
 * {@link NotificationModel} — see this file's doc comment.
 *
 * @param notification the notification as the store holds it
 * @param customerAddress the address `notification.customerAddressId` resolved to
 * @param addressSet the addresses `notification.addressIds` resolved to
 */
export function notificationStateToOdata(
    notification: NotificationState,
    customerAddress: AddressState,
    addressSet: readonly AddressState[],
): ODataNotification {
    // The identifier the payload repeats on nearly every expanded row, which the rows do not store.
    const id = notification.notificationId;

    return {
        __metadata: SYNTHETIC_METADATA,

        FrontId: id.frontId,
        NotificationNo: id.qmNum,
        TaskNumber: id.maNum,

        FunctionalLoc: notification.functionalLocation,
        MessagetypeCSE: notification.messageType,
        CompanyCode: notification.companyCode,
        NotificationType: notification.notificationType,
        InvitroInd: notification.processIndicator,
        UserStatus: notification.userStatus,
        TaskGroup: notification.taskGroup,
        Notification: notification.mqQmnum,
        SalesDocType: notification.orderType,
        ActivityNo: numericField(notification.activityNumber),
        SalesOrg: notification.salesOrg,
        LanguageId: notification.languageId,
        LanguageText: notification.languageText,
        DelivType: notification.deliveryType,
        Effect: notification.effect,
        AnlInaktCode: notification.anlInaktCode,
        Assigned: notification.assigned,
        Dispatcher: notification.dispatcher,
        NdpVersion: numericField(notification.ndpVersion),
        PreClarified: numericField(notification.preclarified),
        RepairLimit: numericField(notification.repairLimit),
        Status: notification.backendStatusCode,

        PlanStartDate: notification.plannedStartDate,
        PlanStartTime: notification.plannedStartTime,
        PlanEndDate: notification.plannedEndDate,
        PlanEndTime: notification.plannedEndTime,
        StartMalfunctionDate: notification.startMalfunctionDate,
        StartMalfunctionTime: notification.startMalfunctionTime,
        UTCDiff: notification.timeZone.utcDiff,
        UTCSign: notification.timeZone.utcSign,
        TimeZone: notification.timeZone.name,

        // The customer address, back in the flat header fields `AddressModel.fromOData` reads.
        Name1: customerAddress.name1shortP58,
        Name2: customerAddress.name2shortP58,
        Name3: '',
        Name4: '',
        Name1Long: customerAddress.name1,
        Name2Long: customerAddress.name2,
        Name3Long: customerAddress.name3,
        Name4Long: customerAddress.name4,
        Street: customerAddress.street,
        // A US state the slice already split off `city` has to go back on as the `<city>;<state>`
        // suffix `AddressModel.fromOData`/`updateStateOfUSA` parses it from — otherwise a state
        // resolved once (e.g. by opening the customer address) is lost on every rebuild. Mirrors the
        // reverse encoding `mapAddress` applies in `buildTimesheetOutModel`.
        City: customerAddress.stateOfUSA
            ? `${customerAddress.city};${customerAddress.stateOfUSA}`
            : customerAddress.city,
        PostCode: customerAddress.postCode,
        Country: customerAddress.country,
        Building: customerAddress.building,
        Room: customerAddress.room,
        Floor: customerAddress.floor,
        Department: customerAddress.department,
        ContactName1: customerAddress.contactName1,
        ContactName2: customerAddress.contactName2,
        ContactTel: customerAddress.contactPhone,
        ContactType: customerAddress.contactType,
        TelNumber: customerAddress.email,
        FaxNumber1: customerAddress.fax1,
        FaxNumber2: customerAddress.fax2,
        FaxNumber3: customerAddress.fax3,
        FaxNumber4: customerAddress.fax4,
        AddrNumber: outboundAddrNumber(customerAddress.addrNumber),

        TelRemote: notification.phoneRemote,
        CustPONo: notification.customerPurchaseOrderNumber,
        NotificationText: notification.notificationText,
        ClarifyText: notification.clarifyText,
        ClarifyText1: notification.clarifyText1,

        Plant: '',
        Division: '',

        AddressSet: wrap(
            addressSet.map((address) => ({
                AddrNumber: outboundAddrNumber(address.addrNumber),
                SearchTerm: '',
                Street: address.street,
                PostalCode: address.postCode,
                City: address.city,
                Country: address.country,
                AddrNotes: address.addrText,
                Counter: 0,
            })),
        ),
        CodeSet: wrap(notification.codes.map(notificationCodeToOdata)),
        ContractSet: wrap(notification.contracts.map((row) => notificationContractRowToOdata(row, id))),
        DopSet: wrap(notification.dropOffPoints.map((dop) => notificationDropOffPointToOdata(dop, id))),
        EmailSet: wrap(notification.emails.map((email) => notificationEmailToOdata(email, id))),
        EquipmentsSet: wrap(notification.equipments.map(notificationEquipmentToOdata)),
        EquipmentCount: notification.equipmentCount,
        HistorySet: wrap(notification.history.map(notificationHistoryEntryToOdata)),
        PartSet: wrap(notification.parts.map(notificationPartToOdata)),
        ParticipantSet: wrap(
            notification.participants.map((participant) => notificationParticipantToOdata(participant, id)),
        ),
        TaskSet: wrap(notification.tasks.map((task) => notificationTaskToOdata(task, id))),
        PaperWorkSet: wrapOptional(notification.paperwork.map(notificationPaperworkQuestionToOdata)),
        FormsCount: notification.formsCount,

        NotifLtextSet: wrap(notificationLongTextLinesToOdata(notification.notificationLines, id)),
        ObjInfLtextSet: wrap(notificationLongTextLinesToOdata(notification.objectInformationLines, id)),
        PreClLtextSet: wrap(notificationLongTextLinesToOdata(notification.preclarificationLines, id)),
        HistoryLtextSet: wrap(notificationLongTextLinesToOdata(notification.historyLines, id)),
        HotsiteLtextSet: wrap(notificationLongTextLinesToOdata(notification.hotsiteLines, id)),
        IntInfLtextSet: wrap(notificationLongTextLinesToOdata(notification.internalInfoLines, id)),
        IssueRepLtextSet: wrapOptional(notificationLongTextLinesToOdata(notification.issueReportLines, id)),
        RentLtextSet: wrapOptional(notificationLongTextLinesToOdata(notification.rentedMaterialLines, id)),
        TaxiLtextSet: wrapOptional(notificationLongTextLinesToOdata(notification.taxiLines, id)),
        SorcLTextSet: wrapOptional(notificationLongTextLinesToOdata(notification.sourceReplacementLines, id)),
    };
}

/**
 * Build the legacy {@link TaskModel} of a single stored task — the one sub-model a caller still asks
 * for on its own, through `NotificationNgrxService.getNotificationTaskByStatusType`. Goes through the
 * payload form rather than the model's constructor, so the mapping stays the one
 * `notificationStateToOdata` uses for the whole task set.
 *
 * @param task the task as the store holds it
 * @param id the notification it belongs to
 */
export function notificationTaskToModel(task: NotificationTaskState, id: NotificationIdState): TaskModel {
    return TaskModel.fromOData(notificationTaskToOdata(task, id));
}

/**
 * Build a {@link NotificationModel} for a stored notification. Used only at the
 * `NotificationNgrxService` boundary — see the model-cache note there for why the service memoizes
 * the result rather than calling this on every read.
 *
 * @param notification the notification as the store holds it
 * @param customerAddress the address `notification.customerAddressId` resolved to
 * @param addressSet the addresses `notification.addressIds` resolved to
 */
export function entryToNotificationModel(
    notification: NotificationState,
    customerAddress: AddressState,
    addressSet: readonly AddressState[],
): NotificationModel {
    const model = new NotificationModel(notificationStateToOdata(notification, customerAddress, addressSet));
    model.updatedStatus = statusStateToModel(effectiveStatusForEntry(notification));
    return model;
}

/** Enough of a long-text row's OData envelope to satisfy the type; nothing reads it back out. */
const SYNTHETIC_LONGTEXT_METADATA: ODataLongText['__metadata'] = { id: '', uri: '', type: '' };

/**
 * One synthetic `IntInfLtextSet` row: the fields {@link odataToNotificationLongTextLines} reads,
 * with an envelope nothing reads.
 *
 * @param textId the row's `TextID`
 * @param counter the row's `Counter`, i.e. its position within its `textId` group
 * @param text the row's `TextLine`
 */
function internalInfoRow(textId: string, counter: number, text: string): ODataLongText {
    return {
        NotificationNo: '',
        TaskNumber: '',
        TextID: textId,
        __metadata: SYNTHETIC_LONGTEXT_METADATA,
        Counter: counter,
        TextLine: text,
    };
}

/**
 * The inverse of `InternalInfoCategoryModel.buildCategoryModel` / `InternalInfoContactModel.buildContactModel`:
 * the `internalInfoLines` a freshly-edited `InternalInfoModel` would produce, so a caller that just
 * saved a legacy internal-info edit can write it back onto the notification's entry (see
 * `updateNotificationInternalInfoLines`) and have a model rebuilt from that entry read the same
 * categories and contacts back.
 *
 * One row per category text line (`ZI01`-`ZI06`, `Counter`ed by the line's position within the
 * category) and one row per contact field per contact (`ZC01`-`ZC07`, `Counter`ed by the contact's
 * position) — the same shape and the same `Counter` convention the two `build…Model` methods read.
 * The "contact" entry of `categoryModel` carries no text of its own (it only supplies the tab's
 * icon/label), so it is skipped; its data lives on `contactModel`. A contact left invalid is skipped
 * too, matching what `InternalInfoModel.getOutModel()` actually submits to the backend.
 *
 * @param model the internal-info model a legacy component edited in place
 */
export function internalInfoModelToLongTextLines(model: InternalInfoModel): NotificationLongTextLineState[] {
    const categoryRows = model.categoryModel
        .getAllCategoriesModel()
        .filter((category) => category.textId.length > 0)
        .flatMap((category) =>
            category.textLine.getLines().map((line, index) => internalInfoRow(category.textId, index, line)),
        );

    const contactRows = model.contactModel.value
        .filter((contact) => contact.model.formValidationStatus !== ConfigConstants.FORM_STATUS.INVALID)
        .flatMap((contact, contactIndex) =>
            contact.model.contactDetails.map((field) =>
                internalInfoRow(field.textId, contactIndex, field.textLine.value),
            ),
        );

    return odataToNotificationLongTextLines([...categoryRows, ...contactRows]);
}

/**
 * Write the fields a `NotificationModel` cannot fill from the payload alone onto it — the DISP/ETA
 * start and end, the effective status, and the four indicator flags — which
 * {@link entryToNotificationModel} therefore leaves empty. The status is the exception: a model built
 * from an entry already carries it, so a snapshot without one leaves that in place rather than
 * clearing it.
 *
 * Every consumer that hands a store-built model on to a model-based collaborator goes through here,
 * so the set of fields cannot drift between them.
 *
 * @param notification the model to complete, mutated in place
 * @param snapshot the fields to apply, from `selectNotificationPropertiesSnapshotById`
 * @returns the same model, for call chaining
 */
export function applyNotificationPropertiesSnapshot(
    notification: NotificationModel,
    snapshot: NotificationPropertiesSnapshot,
): NotificationModel {
    if (snapshot.dispOrEtaStart) {
        notification.dispOrEtaStart = snapshot.dispOrEtaStart;
    }
    if (snapshot.dispOrEtaEnd) {
        notification.dispOrEtaEnd = snapshot.dispOrEtaEnd;
    }
    if (snapshot.updatedStatus) {
        notification.updatedStatus = statusStateToModel(snapshot.updatedStatus);
    }
    notification.isEscalated = snapshot.isEscalated;
    notification.isCancelled = snapshot.isCancelled;
    notification.isUnread = snapshot.isUnread;
    notification.isPaused = false; // "paused" status no longer supported in the app
    return notification;
}
